import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { api, ApiError, Button, Card, ConfirmDialog, Field, PillButton, SaveFlash, apiErrorMessage, formatMoney } from '@aralere/shared-ui';
import type {
  AdjustmentStatus,
  ApplyCreditsRequest,
  CreateAdjustmentRequest,
  CreditOrAdjustment,
  Invoice,
  MoneyRegisterInvoiceRef,
  MoneyRegisterKind,
  MoneyRegisterResponse,
  MoneyRegisterRow,
  Payment,
  PaymentAllocationView,
  PaymentMethod,
  PaymentStatus,
  WouldReleaseDocument,
} from '@aralere/shared-types';

// THE MONEY REGISTER (04 #93 slice (b), the SPA half; server half s213 at GET mgmt-payments?action=money).
// This page BEGAN as the 04 #70 payments register (s93/s96) and keeps that register's whole behaviour
// as its "Payments" type setting: same rows, same order, same filters. 02d #93 ruling 3 (Option A,
// owner re-ruled s213 against the s102 capture) merges every money DOCUMENT into one date-ordered
// stream, so a payment and the client credit born of its own overpayment sit on adjacent rows, and a
// credit note, a refund, a write-off and an early-pay discount are findable by their own number
// without knowing which invoice they touched. Route unchanged (/admin/payments, ruling 2); the nav,
// the dashboard card and the heading read MONEY (owner, s213); adminSections.ts is the single source.
//
// ROW ANATOMY IS LOCKED (ruling 8 as extended s213): type chip · FULL document number (NEVER
// `truncate` on a number anywhere, 04 #93(i)) · plain-language line · the cash-versus-non-cash CUE ·
// the document's OWN face value with its direction sign. Signs are the document's own, not one
// convention: a payment is +$X (cash in); a refund, credit note, early-pay discount, write-off and
// client credit are -$X (each reduces what the client owes when it bites; owner-ruled s215). The cue
// vocabulary is the s213 addendum's verbatim: a credit note and an applied client credit both reduce
// a balance and neither moves cash, but only the credit note touches revenue (the s99 confusion).
// A client credit's applications are DATED sub-lines UNDER its row, never rows of their own: a row is
// a document.
//
// SIX KIND-CONDITIONAL FIGURES AND NEVER ONE NET TOTAL (ruling 7 as amended s213): cash received ·
// cash returned · revenue reversed · discounts given · credits applied "of $Y issued" · written off.
// Each is the SERVER's figure over this page (02 s97: the SPA restates no money figure), rendered only
// when its kind is on the page, $0.00 when every row of the kind is voided.
//
// FILTERS ARE CONDITIONAL (ruling 6): method and the payment status vocabulary render only with
// payments in scope, adjustment status only with an adjustment kind in scope. The server REJECTS an
// out-of-scope filter (400 filter_out_of_scope) rather than ignoring it, so deselecting a kind here
// CLEARS the filters that would go out of scope with it. The client filter matches the document's
// PARTITION (the paying entity for a payment, the mint entity for an adjustment; s213 P7).
//
// READ-ONLY BY CONSTRUCTION, ONE DIALOG DEEP (ruling 10 as amended s213). The register itself mutates
// nothing (no cache to patch, so the 04 #77 stale-cache class cannot arise; every mutation below ends
// in a full refetch). The four actions of the Invoices-page Credits & refunds panel this slice DELETES
// land here or in the invoice detail modal, none dropped: NEW CLIENT CREDIT on the header; VOID and
// the credit-first APPLY (the 04 #73(b) picker, 04b #88) in the per-document dialog, opened from an
// adjustment's number and reading the document FRESH (the #70 receipt-dialog pattern); UN-APPLY in
// the invoice detail modal (Invoices.tsx), where each dated application sub-line here links through.
// When a void is blocked by order (a credit note paired with a live refund; an early-pay discount or
// overpayment credit reversed only through its payment; a credit in use), the dialog SAYS the order
// and offers the step in place where the step is an adjustment void, or links to where it lives.
//
// Voided and failed rows stay inline and are marked, never sorted away (#70, owner-ruled); the hide
// toggle is a display filter over the loaded page and says how many it hid. The fetch is keyed on
// location.key so navigating back after a void re-reads, with an explicit Refresh beside it.

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';
const chipClass = 'rounded-full px-2 py-0.5 text-[11px] leading-4 font-medium';
const linkClass = 'font-medium text-[var(--color-text)] underline decoration-dotted underline-offset-2 hover:decoration-solid';
const errorBoxClass = 'rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]';

const METHOD_LABEL: Record<string, string> = {
  zelle: 'Zelle', check: 'Check', 'money-order': 'Money order', cash: 'Cash', stripe: 'Stripe', square: 'Square', other: 'Other',
};
const METHOD_OPTIONS: PaymentMethod[] = ['zelle', 'check', 'money-order', 'cash', 'stripe', 'square'];
const STATUS_OPTIONS: { value: PaymentStatus; label: string }[] = [
  { value: 'received', label: 'Received' },
  { value: 'pending-confirmation', label: 'Reported by client' },
  { value: 'voided', label: 'Voided' },
  { value: 'failed', label: 'Not received' },
];
const ADJ_STATUS_OPTIONS: { value: AdjustmentStatus; label: string }[] = [
  { value: 'issued', label: 'Issued' },
  { value: 'voided', label: 'Voided' },
];
const PAGE_SIZES = [25, 50, 100, 250];

/** The six kinds in display order (the server's MONEY_KINDS; vendor-credit has no writer and is absent). */
const KIND_ORDER: MoneyRegisterKind[] = ['payment', 'refund', 'credit-note', 'early-pay-discount', 'client-credit', 'write-off'];
const KIND_LABEL: Record<MoneyRegisterKind, string> = {
  payment: 'Payment',
  refund: 'Refund',
  'credit-note': 'Credit note',
  'early-pay-discount': 'Early-pay discount',
  'client-credit': 'Client credit',
  'write-off': 'Write-off',
};
const KIND_FILTER_LABEL: Record<MoneyRegisterKind, string> = {
  payment: 'Payments',
  refund: 'Refunds',
  'credit-note': 'Credit notes',
  'early-pay-discount': 'Early-pay discounts',
  'client-credit': 'Client credits',
  'write-off': 'Write-offs',
};
/** ONE colour per kind, used identically on the row's type chip, the Type filter chip and the figure strip's
 *  label (owner, s215), so the three surfaces read as one vocabulary. The colours follow the money: cash in
 *  green, cash out orange, a loss red, a discount amber, a held credit blue, a revenue reversal slate. */
type KindTone = { chip: string; text: string; filter: string; filterOn: string };
const KIND_TONE: Record<MoneyRegisterKind, KindTone> = {
  payment: {
    chip: 'bg-[var(--tint-green-bg-soft)] text-[var(--tint-green-text)]',
    text: 'text-[var(--tint-green-text)]',
    filter: 'border-[var(--tint-green-border)] text-[var(--tint-green-text)] hover:bg-[var(--tint-green-bg-soft)]',
    filterOn: 'border-[var(--tint-green-text)] bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]',
  },
  refund: {
    chip: 'bg-[var(--tint-orange-bg)] text-[var(--tint-orange-text)]',
    text: 'text-[var(--tint-orange-text)]',
    filter: 'border-[var(--tint-orange-text)] text-[var(--tint-orange-text)] hover:bg-[var(--tint-orange-bg)]',
    filterOn: 'border-[var(--tint-orange-text)] bg-[var(--tint-orange-bg)] text-[var(--tint-orange-text)]',
  },
  'credit-note': {
    chip: 'bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)]',
    text: 'text-[var(--tint-slate-text)]',
    filter: 'border-[var(--tint-slate-text)] text-[var(--tint-slate-text)] hover:bg-[var(--tint-slate-bg)]',
    filterOn: 'border-[var(--tint-slate-text)] bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)]',
  },
  'early-pay-discount': {
    chip: 'bg-[var(--tint-warn-bg-soft)] text-[var(--tint-warn-text)]',
    text: 'text-[var(--tint-warn-text)]',
    filter: 'border-[var(--tint-warn-border)] text-[var(--tint-warn-text)] hover:bg-[var(--tint-warn-bg-soft)]',
    filterOn: 'border-[var(--tint-warn-text)] bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]',
  },
  'client-credit': {
    chip: 'bg-[var(--tint-blue-bg-soft)] text-[var(--tint-blue-text)]',
    text: 'text-[var(--tint-blue-text)]',
    filter: 'border-[var(--tint-blue-border)] text-[var(--tint-blue-text)] hover:bg-[var(--tint-blue-bg-soft)]',
    filterOn: 'border-[var(--tint-blue-text)] bg-[var(--tint-blue-bg)] text-[var(--tint-blue-text)]',
  },
  'write-off': {
    chip: 'bg-[var(--tint-red-bg-soft)] text-[var(--tint-red-text)]',
    text: 'text-[var(--tint-red-text)]',
    filter: 'border-[var(--tint-red-border)] text-[var(--tint-red-text)] hover:bg-[var(--tint-red-bg-soft)]',
    filterOn: 'border-[var(--tint-red-text)] bg-[var(--tint-red-bg)] text-[var(--tint-red-text)]',
  },
};

type AdjustmentRow = Extract<MoneyRegisterRow, { adjustment: CreditOrAdjustment }>;
type PaymentRow = Extract<MoneyRegisterRow, { payment: Payment }>;

/** One editable row of the credit-first Apply picker (04 #73(b); the 04 #88 CrossRow shape). */
type ApplyRow = {
  invoiceId: string;
  /** The invoice's OWN partition owner (04 #88): the picker spans a billing entity's linked people. */
  clientId: string;
  documentNumber: string | null;
  issueDate: string;
  dueDate: string;
  remaining: number;
  pastDue: boolean;
  amount: string;
};

/** YYYY-MM-DD -> MM-DD-YYYY for display (the Invoices page's form). */
function displayDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${m}-${d}-${y}`;
}

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** A voided or failed receipt is not money (#70). */
const isLivePayment = (s: PaymentStatus): boolean => s === 'received' || s === 'pending-confirmation';

/** What is left on a client credit: amount less what it has been applied so far. 0 for every other kind. */
const creditRemaining = (a: CreditOrAdjustment): number =>
  a.kind === 'client-credit' ? round2(Math.max(0, a.amount - (a.amountApplied ?? 0))) : 0;

/** Deep link to an invoice's Payments panel on the admin Invoices page (#70). `clientId` is the
 *  INVOICE's own partition, which is why every allocation and invoice ref carries it. */
function invoicePaymentsLink(clientId: string, invoiceId: string): string {
  return `/admin/invoices?client=${encodeURIComponent(clientId)}&open=${encodeURIComponent(invoiceId)}&payments=1`;
}

/** Deep link to an invoice's DETAIL modal (04 #93 (b)): the Invoices page consumes `detail=1` the way
 *  it consumes `payments=1`. This is where an applied client credit is listed with its applied date and
 *  its Un-apply pill (ruling 10), so an application sub-line here links straight to that surface. */
function invoiceDetailLink(clientId: string, invoiceId: string): string {
  return `/admin/invoices?client=${encodeURIComponent(clientId)}&open=${encodeURIComponent(invoiceId)}&detail=1`;
}

/** Distribute the credit's remaining across rows OLDEST-first (rows arrive oldest-first from the server),
 *  filling each to its remaining until the pot is exhausted (owner rule: oldest-first pre-fill). */
function prefillOldestFirst(rows: ApplyRow[], pot: number): ApplyRow[] {
  let left = round2(Math.max(0, pot));
  return rows.map((r) => {
    const take = round2(Math.max(0, Math.min(r.remaining, left)));
    left = round2(left - take);
    return { ...r, amount: take > 0 ? String(take) : '' };
  });
}

/** A client credit's applications: its own applications[] (04 #73) or, for a legacy credit with none,
 *  the singular appliedToInvoiceId anchor rendered as one entry. */
function applicationsOf(a: CreditOrAdjustment, numberOf: (invoiceId: string) => string | null) {
  if (a.kind !== 'client-credit') return [];
  if (a.applications && a.applications.length > 0) return a.applications;
  if (a.appliedToInvoiceId && (a.amountApplied ?? 0) > 0) {
    return [{
      applicationId: a.id,
      invoiceId: a.appliedToInvoiceId,
      invoiceNumber: numberOf(a.appliedToInvoiceId),
      amount: round2(a.amountApplied ?? 0),
      appliedAt: a.appliedAt ?? a.createdAt,
    }];
  }
  return [];
}

/** Pull the protected documents off a would_release_documents 409, or null if that is not the error. */
function releaseDocsFromError(err: unknown): WouldReleaseDocument[] | null {
  if (err instanceof ApiError) {
    const d = err.details as
      | { error?: { code?: string; details?: { documents?: WouldReleaseDocument[] } } }
      | null
      | undefined;
    if (d?.error?.code === 'would_release_documents') return d.error.details?.documents ?? [];
  }
  return null;
}

const UNRESOLVED_PARTITION =
  'Could not work out which client this document belongs to. Reload the page and try again.';

export function AdminPayments(): JSX.Element {
  const location = useLocation();

  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [kinds, setKinds] = useState<MoneyRegisterKind[]>([]);
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const [adjustmentStatus, setAdjustmentStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [hideVoided, setHideVoided] = useState(false);

  const [data, setData] = useState<MoneyRegisterResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  /** Bumped by Refresh and after every mutation. A plain refetch cannot ride the filter state, because
   *  re-setting a value to what it already is makes React bail out and the effect never re-runs. */
  const [reloadKey, setReloadKey] = useState(0);
  /** The filtered client's available credit (s213 P3): read from listAdjustments(clientId), the one
   *  server figure for it; null when no single client is filtered. Never computed here (02 s97). */
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // New client credit (the former Invoices-page openNewCredit / doNewCredit). The page is not
  // client-scoped, so the dialog carries its own client picker, prefilled from the filter.
  const [newCreditForm, setNewCreditForm] = useState<{ clientId: string; amount: string; reason: string } | null>(null);
  const [newCreditError, setNewCreditError] = useState<string | null>(null);

  // The per-document dialog: the row it opened from, the FRESH read (null while loading), and the
  // invoice the fresh read resolved (an adjusted invoice's number for the header line).
  const [doc, setDoc] = useState<{ row: AdjustmentRow; fresh: CreditOrAdjustment | null; invoice: Invoice | null; error: string | null } | null>(null);

  // Void (the former voidAdj* state of Invoices.tsx, verbatim semantics).
  const [voidTarget, setVoidTarget] = useState<CreditOrAdjustment | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidAlsoCN, setVoidAlsoCN] = useState(true);
  const [voidError, setVoidError] = useState<string | null>(null);

  // The credit-first Apply picker (04 #73(b); the former applyForm of Invoices.tsx).
  const [applyForm, setApplyForm] = useState<{ adjustment: CreditOrAdjustment; rows: ApplyRow[]; loading: boolean } | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [releaseHeadsUp, setReleaseHeadsUp] = useState<{ documents: WouldReleaseDocument[]; retry: () => Promise<void> } | null>(null);

  // Client names are resolved CLIENT-SIDE from one listClients call (s93): one request per page load
  // instead of a point-read per distinct partition on every register query.
  useEffect(() => {
    void (async () => {
      try {
        const res = await api.admin.listClients();
        const map: Record<string, string> = {};
        for (const row of res.clients) map[row.client.id] = row.client.displayName;
        setClientNames(map);
      } catch {
        /* names degrade to ids; the register itself still renders */
      }
    })();
  }, []);

  // One debounced fetch for every filter, the page window, and any navigation back to this route.
  useEffect(() => {
    const t = setTimeout(() => {
      void (async () => {
        setLoading(true);
        setErr(null);
        try {
          setData(
            await api.admin.moneyRegister({
              kinds,
              q: q.trim() || null,
              clientId: clientId || null,
              method: (method as PaymentMethod) || null,
              status: (status as PaymentStatus) || null,
              adjustmentStatus: (adjustmentStatus as AdjustmentStatus) || null,
              from: from || null,
              to: to || null,
              page,
              limit,
            }),
          );
        } catch (e) {
          setErr(apiErrorMessage(e));
          setData(null);
        } finally {
          setLoading(false);
        }
      })();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, clientId, kinds, method, status, adjustmentStatus, from, to, page, limit, reloadKey, location.key]);

  // The filtered client's available credit, re-read after every mutation (the pot an Apply spends).
  useEffect(() => {
    if (!clientId) {
      setCreditBalance(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.admin.listAdjustments(clientId);
        if (!cancelled) setCreditBalance(res.clientCreditBalance);
      } catch {
        if (!cancelled) setCreditBalance(null);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, reloadKey]);

  /** Any filter change restarts at page 1 (#70): staying on page 7 of a result set that just shrank
   *  would be silently clamped by the server and read as a filter that did nothing. */
  const onFilter = <T,>(set: (v: T) => void) => (v: T): void => {
    setPage(1);
    set(v);
  };

  const paymentsInScope = kinds.length === 0 || kinds.includes('payment');
  const adjustmentsInScope = kinds.length === 0 || kinds.some((k) => k !== 'payment');

  /** Toggle one kind in the type filter. Ruling 6 in the other direction: when the toggle takes payments
   *  (or every adjustment kind) OUT of scope, the filters that only apply to them are cleared, because
   *  the server refuses an out-of-scope filter rather than ignoring it. */
  const toggleKind = (k: MoneyRegisterKind): void => {
    const next = kinds.includes(k) ? kinds.filter((x) => x !== k) : [...KIND_ORDER.filter((x) => kinds.includes(x) || x === k)];
    const nextPayments = next.length === 0 || next.includes('payment');
    const nextAdjustments = next.length === 0 || next.some((x) => x !== 'payment');
    setPage(1);
    setKinds(next);
    if (!nextPayments) {
      setMethod('');
      setStatus('');
    }
    if (!nextAdjustments) setAdjustmentStatus('');
  };

  const clientOptions = useMemo(
    () => Object.entries(clientNames).sort((a, b) => a[1].localeCompare(b[1])),
    [clientNames],
  );

  const rows = data?.rows ?? [];
  const isDead = (r: MoneyRegisterRow): boolean =>
    r.kind === 'payment' ? !isLivePayment(r.payment.status) : r.adjustment.status === 'voided';
  const visible = hideVoided ? rows.filter((r) => !isDead(r)) : rows;
  const hiddenCount = rows.length - visible.length;
  const totalPages = data?.totalPages ?? 0;
  const totalCount = data?.totalCount ?? 0;
  const filtersActive = !!(q.trim() || clientId || kinds.length > 0 || method || status || adjustmentStatus || from || to);

  const allocationsFor = (paymentId: string): PaymentAllocationView[] => data?.allocationContext?.[paymentId] ?? [];
  const clientName = (id: string | null | undefined): string => (id ? clientNames[id] ?? id : 'this client');
  const invoiceRef = (id: string | null | undefined): MoneyRegisterInvoiceRef | null => (id ? data?.invoices[id] ?? null : null);
  const invoiceNumberOf = (id: string): string | null => invoiceRef(id)?.documentNumber ?? null;

  /** An invoice an adjustment names, linking through to its detail modal when the server resolved it.
   *  An id the `invoices` map lacks (a document that no longer resolves) prints as the id, plainly. */
  const invoiceLink = (id: string | null | undefined, target: 'detail' | 'payments' = 'detail'): JSX.Element | null => {
    if (!id) return null;
    const ref = invoiceRef(id);
    if (!ref) return <span className="font-medium">{id}</span>;
    return (
      <Link className={linkClass} to={target === 'payments' ? invoicePaymentsLink(ref.clientId, id) : invoiceDetailLink(ref.clientId, id)}>
        {ref.documentNumber ?? id}
      </Link>
    );
  };

  // ── Page-level exceptions per kind, for the figure strip's sub-notes. Counts, never money: the money
  //    figures are the server's. (The one money sub-note, tips inside cash received, is a sum of a stored
  //    field over the page's rows exactly as #70 printed it.)
  const paymentRows = rows.filter((r): r is PaymentRow => r.kind === 'payment');
  const paymentsVoided = paymentRows.filter((r) => r.payment.status === 'voided').length;
  const paymentsFailed = paymentRows.filter((r) => r.payment.status === 'failed').length;
  const paymentsPending = paymentRows.filter((r) => r.payment.status === 'pending-confirmation').length;
  const tipsOnPage = round2(paymentRows.filter((r) => r.payment.status === 'received').reduce((s, r) => s + (r.payment.tipAmount ?? 0), 0));
  const voidedOfKind = (k: MoneyRegisterKind): number =>
    rows.filter((r) => r.kind !== 'payment' && r.kind === k && r.adjustment.status === 'voided').length;

  const clearFilters = (): void => {
    setPage(1);
    setQ('');
    setClientId('');
    setKinds([]);
    setMethod('');
    setStatus('');
    setAdjustmentStatus('');
    setFrom('');
    setTo('');
  };

  const refetch = (): void => setReloadKey((k) => k + 1);

  // ── New client credit ────────────────────────────────────────────────────────────────────────────
  const openNewCredit = (): void => {
    setNewCreditError(null);
    setNewCreditForm({ clientId, amount: '', reason: '' });
  };

  const doNewCredit = async (): Promise<void> => {
    if (!newCreditForm) return;
    const amount = round2(parseFloat(newCreditForm.amount) || 0);
    if (!newCreditForm.clientId) return setNewCreditError('Choose the client the credit is for.');
    if (!(amount > 0)) return setNewCreditError('A positive amount is required.');
    if (!newCreditForm.reason.trim()) return setNewCreditError('A reason is required.');
    const body: CreateAdjustmentRequest = {
      kind: 'client-credit',
      clientId: newCreditForm.clientId,
      amount,
      reason: newCreditForm.reason.trim(),
    };
    setBusy(true);
    setNewCreditError(null);
    try {
      const res = await api.admin.createAdjustment(body);
      setNewCreditForm(null);
      refetch();
      setFlash(`Client credit ${res.adjustment.documentNumber ?? ''} issued for ${clientName(newCreditForm.clientId)}.`);
    } catch (e) {
      setNewCreditError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // ── The per-document dialog ──────────────────────────────────────────────────────────────────────
  /** Open an adjustment's dialog and read the document FRESH (#70: what an admin sees on a financial
   *  surface is server truth, never accumulated page state). */
  const openDoc = (row: AdjustmentRow): void => {
    const a = row.adjustment;
    setDoc({ row, fresh: null, invoice: null, error: a.clientId ? null : UNRESOLVED_PARTITION });
    if (!a.clientId) return;
    void (async () => {
      try {
        const res = await api.admin.getAdjustment(a.id, a.clientId!);
        setDoc((d) => (d && d.row.adjustment.id === a.id ? { ...d, fresh: res.adjustment, invoice: res.invoice ?? null } : d));
      } catch (e) {
        setDoc((d) => (d && d.row.adjustment.id === a.id ? { ...d, error: apiErrorMessage(e) } : d));
      }
    })();
  };

  const closeDoc = (): void => setDoc(null);

  const startVoid = (a: CreditOrAdjustment): void => {
    setVoidTarget(a);
    setVoidReason('');
    setVoidError(null);
    setVoidAlsoCN(true);
  };

  const doVoid = async (): Promise<void> => {
    if (!voidTarget) return;
    if (!voidReason.trim()) return setVoidError('A void reason is required.');
    const cascade = voidTarget.kind === 'refund' && !!voidTarget.linkedCreditNoteId && voidAlsoCN;
    const partition = voidTarget.clientId;
    if (!partition) return setVoidError(UNRESOLVED_PARTITION);
    setBusy(true);
    setVoidError(null);
    try {
      await api.admin.voidAdjustment(voidTarget.id, partition, voidReason.trim(), cascade);
      setVoidTarget(null);
      setVoidReason('');
      setDoc(null);
      refetch();
      setFlash(cascade ? `Refund ${voidTarget.documentNumber ?? ''} and its credit note voided.` : `${KIND_LABEL[voidTarget.kind as MoneyRegisterKind] ?? 'Document'} ${voidTarget.documentNumber ?? ''} voided.`);
    } catch (e) {
      setVoidError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** Open the credit-first Apply picker (04 #73(b)): the credit's billing entity's open invoices (the
   *  same server rows the #64 cross-payment picker uses; entity = adj.clientId), prefilled oldest-first
   *  from the credit's remaining. Every row stays editable; whatever is not applied stays on the credit. */
  const openApply = (adj: CreditOrAdjustment): void => {
    setApplyError(null);
    if (!adj.clientId) {
      setDoc((d) => (d ? { ...d, error: UNRESOLVED_PARTITION } : d));
      return;
    }
    setDoc(null);
    setApplyForm({ adjustment: adj, rows: [], loading: true });
    void (async () => {
      try {
        const res = await api.admin.allocatableInvoices(adj.clientId!);
        const rowsIn: ApplyRow[] = res.invoices.map((i) => ({
          invoiceId: i.id,
          clientId: i.clientId,
          documentNumber: i.documentNumber,
          issueDate: i.issueDate,
          dueDate: i.dueDate,
          remaining: i.remaining,
          pastDue: i.pastDue,
          amount: '',
        }));
        setApplyForm((f) => (f && f.adjustment.id === adj.id ? { ...f, rows: prefillOldestFirst(rowsIn, creditRemaining(adj)), loading: false } : f));
      } catch (e) {
        setApplyError(apiErrorMessage(e));
        setApplyForm((f) => (f && f.adjustment.id === adj.id ? { ...f, rows: [], loading: false } : f));
      }
    })();
  };

  const setApplyRowAmount = (invoiceId: string, v: string): void => {
    setApplyError(null);
    setApplyForm((f) => (f ? { ...f, rows: f.rows.map((r) => (r.invoiceId === invoiceId ? { ...r, amount: v } : r)) } : f));
  };

  /** ONE request applying the credit across every filled row (fail-fast server validation; an
   *  aggregated would_release_documents 409 retries through the heads-up). */
  const doApply = async (confirmRelease = false): Promise<void> => {
    if (!applyForm) return;
    const adj = applyForm.adjustment;
    const remaining = creditRemaining(adj);
    const applications = applyForm.rows
      .map((r) => ({ invoiceId: r.invoiceId, amount: round2(parseFloat(r.amount) || 0), remaining: r.remaining, number: r.documentNumber }))
      .filter((a) => a.amount > 0);
    if (applications.length === 0) return setApplyError('Enter an amount on at least one invoice.');
    const over = applications.find((a) => a.amount > a.remaining + 0.001);
    if (over) return setApplyError(`Application for ${over.number ?? 'an invoice'} exceeds its remaining balance (${formatMoney(over.remaining)}).`);
    const total = round2(applications.reduce((sum, a) => sum + a.amount, 0));
    if (total > remaining + 0.001) return setApplyError(`The total to apply (${formatMoney(total)}) exceeds the credit's remaining ${formatMoney(remaining)}.`);
    const partition = adj.clientId;
    if (!partition) return setApplyError(UNRESOLVED_PARTITION);
    const body: ApplyCreditsRequest = {
      applications: applications.map((a) => ({ invoiceId: a.invoiceId, amount: a.amount })),
      ...(confirmRelease ? { confirmRelease: true } : {}),
    };
    setBusy(true);
    setApplyError(null);
    try {
      await api.admin.applyCredits(adj.id, partition, body);
      setApplyForm(null);
      setReleaseHeadsUp(null);
      refetch();
      setFlash(`${adj.documentNumber ?? 'Credit'} applied to ${applications.length} invoice${applications.length === 1 ? '' : 's'}.`);
    } catch (e) {
      const docs = releaseDocsFromError(e);
      if (docs) setReleaseHeadsUp({ documents: docs, retry: () => doApply(true) });
      else setApplyError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // ── Row rendering (ruling 8) ─────────────────────────────────────────────────────────────────────

  /** The cue line, the s213 addendum's vocabulary verbatim. */
  const cueFor = (r: MoneyRegisterRow): string => {
    if (r.kind === 'payment') {
      const p = r.payment;
      if (p.status === 'pending-confirmation') return 'Reported by client · not counted';
      return `Cash received · ${METHOD_LABEL[p.method] ?? p.method}`;
    }
    const a = r.adjustment;
    switch (r.kind) {
      case 'refund':
        return `Cash returned · ${a.method ? METHOD_LABEL[a.method] ?? a.method : 'method not recorded'}`;
      case 'credit-note':
        return 'No cash moved · revenue and tax reversed';
      case 'early-pay-discount':
        return `No cash moved · discount granted with ${a.sourcePaymentNumber ?? 'its payment'}`;
      case 'client-credit': {
        const base = creditRemaining(a) > 0 || (a.amountApplied ?? 0) === 0 ? 'No cash moved · prepaid credit held' : 'No cash moved · prepaid credit used';
        return a.sourcePaymentId ? `${base} · from the overpayment on ${a.sourcePaymentNumber ?? 'a payment'}` : base;
      }
      case 'write-off':
        return 'No cash moved · booked as bad debt';
      default:
        return '';
    }
  };

  /** The plain-language line: who, what document it acts on, what it is linked to, and the reason. */
  const plainLineFor = (r: AdjustmentRow): JSX.Element => {
    const a = r.adjustment;
    const who = <span className="text-[var(--color-text)]">{clientName(a.clientId)}</span>;
    switch (r.kind) {
      case 'refund':
        return (
          <>
            Returned to {who}
            {a.invoiceId ? <> · on {invoiceLink(a.invoiceId)}</> : a.sourcePaymentNumber ? <> · the leftover of {a.sourcePaymentNumber}</> : null}
            {a.linkedCreditNoteNumber ? <> · with credit note {a.linkedCreditNoteNumber}</> : null}
            {a.reason ? <> · {a.reason}</> : null}
          </>
        );
      case 'credit-note':
        return (
          <>
            Credits {who} on {invoiceLink(a.invoiceId)}
            {a.linkedRefundNumber ? <> · with refund {a.linkedRefundNumber}</> : null}
            {a.reason ? <> · {a.reason}</> : null}
          </>
        );
      case 'early-pay-discount':
        // The server-written reason already names the rate, the window and the payment, and the cue names the
        // payment again, so the line adds only who and which invoice.
        return (
          <>
            For {who} on {invoiceLink(a.invoiceId)}
            {a.reason ? <> · {a.reason}</> : <> · earned by {a.sourcePaymentNumber ?? 'a payment'}</>}
          </>
        );
      case 'client-credit': {
        const remaining = creditRemaining(a);
        return (
          <>
            Held for {who}
            {a.status === 'issued' ? <> · {formatMoney(remaining)} left</> : null}
            {a.sourceInvoiceId ? <> · overpayment on {invoiceLink(a.sourceInvoiceId)}</> : null}
            {a.reason ? <> · {a.reason}</> : null}
          </>
        );
      }
      case 'write-off':
        return (
          <>
            Written off for {who} on {invoiceLink(a.invoiceId)}
            {a.reason ? <> · {a.reason}</> : null}
          </>
        );
      default:
        return <>{a.reason}</>;
    }
  };

  /** A client credit's dated application sub-lines (ruling 8 as extended s213): where its money went,
   *  one line per invoice, each linking through to that invoice's detail modal (where Un-apply lives). */
  const renderApplications = (a: CreditOrAdjustment): JSX.Element | null => {
    const apps = applicationsOf(a, invoiceNumberOf);
    if (apps.length === 0) return null;
    return (
      <div className="space-y-0.5 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2">
        {apps.map((x) => (
          <div key={x.applicationId} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs">
            <span className="min-w-0 text-[var(--color-muted)]">
              Applied {displayDate(x.appliedAt.slice(0, 10))} to {invoiceLink(x.invoiceId) ?? <span className="font-medium">{x.invoiceNumber ?? x.invoiceId}</span>}
            </span>
            <span className="shrink-0 text-[var(--color-muted)]">{formatMoney(x.amount)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderPaymentRow = (r: PaymentRow): JSX.Element => {
    const p = r.payment;
    const allocs = allocationsFor(p.id);
    const excess = round2(p.creditedExcess ?? 0);
    const tip = round2(p.tipAmount ?? 0);
    const dead = !isLivePayment(p.status);
    const struck = dead ? 'text-[var(--color-muted)] line-through' : '';
    return (
      <Card key={p.id}>
        <div className={`space-y-2 p-4 ${dead ? 'bg-[rgb(var(--edge-rgb)/0.02)]' : ''}`}>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${chipClass} ${KIND_TONE.payment.chip}`}>Payment</span>
                {p.status === 'voided' ? (
                  <span className={`${chipClass} bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]`}>Voided</span>
                ) : p.status === 'failed' ? (
                  <span className={`${chipClass} bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]`}>Not received</span>
                ) : p.status === 'pending-confirmation' ? (
                  <span className={`${chipClass} bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]`}>Reported by client</span>
                ) : null}
                <span className={`break-all font-semibold ${struck}`}>{p.documentNumber ?? 'Reported'}</span>
                <span className="text-sm text-[var(--color-muted)]">received {displayDate(r.date)}</span>
              </div>
              <div className="text-sm text-[var(--color-muted)]">
                Paid by <span className="text-[var(--color-text)]">{clientName(p.clientId)}</span>
                {p.receiptNumber ? <span> · ref {p.receiptNumber}</span> : null}
                {p.memo ? <span> · {p.memo}</span> : null}
              </div>
              <div className={`text-xs text-[var(--color-muted)] ${dead ? 'line-through' : ''}`}>{cueFor(r)}</div>
            </div>
            <div className={`shrink-0 text-right font-semibold ${struck}`}>+{formatMoney(p.amount)}</div>
          </div>

          {/* Every invoice this receipt reached, as equals (#70: receipt-as-hub, no "original invoice"). */}
          {allocs.length > 0 ? (
            <div className="space-y-0.5 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2">
              {allocs.map((a) => (
                <div key={a.invoiceId} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs">
                  <span className="min-w-0">
                    {a.clientId ? (
                      <Link className={linkClass} to={invoicePaymentsLink(a.clientId, a.invoiceId)}>{a.documentNumber ?? a.invoiceId}</Link>
                    ) : (
                      <span className="font-medium text-[var(--color-text)]">{a.documentNumber ?? a.invoiceId}</span>
                    )}
                    {a.clientName ? <span className="text-[var(--color-muted)]"> · {a.clientName}</span> : null}
                  </span>
                  <span className="shrink-0 text-[var(--color-muted)]">{formatMoney(a.amount)}</span>
                </div>
              ))}
              {/* creditedExcess is stamped under BOTH #64 leftover dispositions, so the disposition LINKS
                  decide the wording, never the field (the s91 invariant). */}
              {excess > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs text-[var(--color-muted)]">
                  <span>
                    {p.overpaymentRefundId ? 'Refunded' : p.overpaymentCreditId ? 'To account credit' : 'Unapplied'}
                    {p.overpaymentRefundNumber ? ` (${p.overpaymentRefundNumber})` : p.overpaymentCreditNumber ? ` (${p.overpaymentCreditNumber})` : ''}
                  </span>
                  <span className="shrink-0">{formatMoney(excess)}</span>
                </div>
              ) : null}
              {tip > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs text-[var(--color-muted)]">
                  <span>Tip</span>
                  <span className="shrink-0">{formatMoney(tip)}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {dead ? (
            <div className="border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2 text-xs text-[var(--color-muted)]">
              {p.status === 'voided' ? 'Voided' : 'Marked not received'}
              {p.voidedAt ? ` ${displayDate(p.voidedAt.slice(0, 10))}` : ''}
              {p.returnedPayment ? ' · returned or bounced (NSF)' : ''}
              {p.voidReason ? ` · ${p.voidReason}` : ''}
              {allocs.length > 0 ? '. The amounts above are what it had been applied to before it was reversed.' : ''}
            </div>
          ) : null}
        </div>
      </Card>
    );
  };

  const renderAdjustmentRow = (r: AdjustmentRow): JSX.Element => {
    const a = r.adjustment;
    const dead = a.status === 'voided';
    const struck = dead ? 'text-[var(--color-muted)] line-through' : '';
    return (
      <Card key={a.id}>
        <div className={`space-y-2 p-4 ${dead ? 'bg-[rgb(var(--edge-rgb)/0.02)]' : ''}`}>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${chipClass} ${KIND_TONE[r.kind].chip}`}>{KIND_LABEL[r.kind]}</span>
                {dead ? <span className={`${chipClass} bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]`}>Voided</span> : null}
                {/* The number is the way IN to the document (its dialog carries Void and Apply, ruling 10).
                    FULL number, break-all so a narrow screen wraps it rather than clipping it (04 #93(i)). */}
                <button type="button" className={`break-all text-left font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid ${struck}`} onClick={() => openDoc(r)}>
                  {a.documentNumber ?? 'Unnumbered'}
                </button>
                <span className="text-sm text-[var(--color-muted)]">{r.kind === 'client-credit' ? 'issued ' : ''}{displayDate(r.date)}</span>
              </div>
              <div className="text-sm text-[var(--color-muted)]">{plainLineFor(r)}</div>
              <div className={`text-xs text-[var(--color-muted)] ${dead ? 'line-through' : ''}`}>{cueFor(r)}</div>
            </div>
            <div className="shrink-0 text-right">
              <div className={`font-semibold ${struck}`}>-{formatMoney(a.amount)}</div>
              {/* Owner, s215: the number is the way in, but nothing on the row said so (PMT numbers on this page are
                  plain by design), so the affordance is spelled out. */}
              <button type="button" className="text-xs text-[var(--color-muted)] underline decoration-dotted underline-offset-2 hover:decoration-solid" onClick={() => openDoc(r)}>
                Open
              </button>
            </div>
          </div>
          {r.kind === 'client-credit' ? renderApplications(a) : null}
          {dead ? (
            <div className="border-t border-[rgb(var(--edge-rgb)/0.05)] pt-2 text-xs text-[var(--color-muted)]">
              Voided{a.voidedAt ? ` ${displayDate(a.voidedAt.slice(0, 10))}` : ''}{a.voidReason ? ` · ${a.voidReason}` : ''}
            </div>
          ) : null}
        </div>
      </Card>
    );
  };

  // ── The figure strip (ruling 7 as amended s213): the SERVER's six, kind-conditional. ─────────────
  const figures = data?.figures;
  const figureBlocks: { key: string; kind: MoneyRegisterKind; label: string; value: string; tail?: string; note?: string }[] = [];
  if (figures) {
    if (figures.cashReceived !== null) {
      const notes: string[] = [];
      if (tipsOnPage > 0) notes.push(`of which tips ${formatMoney(tipsOnPage)}`);
      if (paymentsPending > 0) notes.push(`${paymentsPending} reported by a client, not counted`);
      if (paymentsVoided > 0) notes.push(`${paymentsVoided} voided`);
      if (paymentsFailed > 0) notes.push(`${paymentsFailed} not received`);
      figureBlocks.push({ key: 'cashReceived', kind: 'payment', label: 'Cash received', value: formatMoney(figures.cashReceived), note: notes.join(' · ') || undefined });
    }
    const simple = (key: 'cashReturned' | 'revenueReversed' | 'discountsGiven' | 'writtenOff', label: string, kind: MoneyRegisterKind): void => {
      const v = figures[key];
      if (v === null) return;
      const voided = voidedOfKind(kind);
      figureBlocks.push({ key, kind, label, value: formatMoney(v), note: voided > 0 ? `${voided} voided` : undefined });
    };
    simple('cashReturned', 'Cash returned', 'refund');
    simple('revenueReversed', 'Revenue reversed', 'credit-note');
    simple('discountsGiven', 'Discounts given', 'early-pay-discount');
    if (figures.creditsApplied !== null) {
      const voided = voidedOfKind('client-credit');
      figureBlocks.push({
        key: 'creditsApplied',
        kind: 'client-credit',
        label: 'Credits applied',
        value: formatMoney(figures.creditsApplied),
        tail: figures.creditsIssued !== null ? `of ${formatMoney(figures.creditsIssued)} issued` : undefined,
        note: voided > 0 ? `${voided} voided` : undefined,
      });
    }
    simple('writtenOff', 'Written off', 'write-off');
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-display tracking-tight">Money</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Every payment, refund, credit note, client credit, write-off and early-pay discount, in one register, newest first.
          </p>
          <p className="text-sm text-[var(--color-muted)]">
            Search by document number to find one without knowing which invoice it touched.
          </p>
        </div>
        {/* Owner, s215: the creating action is the one primary button; Refresh sits centred above it. */}
        <div className="flex shrink-0 flex-row items-center gap-2 sm:flex-col sm:items-center">
          <Button variant="ghost" size="sm" onClick={refetch} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
          <Button variant="primary" onClick={openNewCredit} disabled={busy}>New client credit</Button>
        </div>
      </div>

      <SaveFlash message={flash} onClear={() => setFlash(null)} />

      <Card>
        <div className="space-y-3 p-4">
          {/* The type filter. None selected = every kind; Payments alone is the #70 register exactly. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-[var(--color-muted)]">Type</span>
            {KIND_ORDER.map((k) => {
              const on = kinds.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleKind(k)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${on ? KIND_TONE[k].filterOn : KIND_TONE[k].filter}`}
                >
                  {on ? '✓ ' : ''}{KIND_FILTER_LABEL[k]}
                </button>
              );
            })}
            {kinds.length > 0 ? (
              <button type="button" className="text-xs text-[var(--color-muted)] underline" onClick={() => onFilter(setKinds)([])}>Every type</button>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Document number</span>
              <input className={inputClass} placeholder="0085, CRN-0012, CRD" value={q} onChange={(e) => onFilter(setQ)(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">Client</span>
              <select className={inputClass} value={clientId} onChange={(e) => onFilter(setClientId)(e.target.value)}>
                <option value="">Any client</option>
                {clientOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </label>
            {/* Ruling 6: payment vocabulary only with payments in scope. */}
            {paymentsInScope ? (
              <label className="flex flex-col gap-1">
                <span className="text-xs text-[var(--color-muted)]">Payment method</span>
                <select className={inputClass} value={method} onChange={(e) => onFilter(setMethod)(e.target.value)}>
                  <option value="">Any method</option>
                  {METHOD_OPTIONS.map((m) => (
                    <option key={m} value={m}>{METHOD_LABEL[m] ?? m}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {paymentsInScope ? (
              <label className="flex flex-col gap-1">
                <span className="text-xs text-[var(--color-muted)]">Payment status</span>
                <select className={inputClass} value={status} onChange={(e) => onFilter(setStatus)(e.target.value)}>
                  <option value="">Any status</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {/* Ruling 6: adjustment status only with an adjustment kind in scope. */}
            {adjustmentsInScope ? (
              <label className="flex flex-col gap-1">
                <span className="text-xs text-[var(--color-muted)]">{paymentsInScope ? 'Credit, refund and write-off status' : 'Status'}</span>
                <select className={inputClass} value={adjustmentStatus} onChange={(e) => onFilter(setAdjustmentStatus)(e.target.value)}>
                  <option value="">Any status</option>
                  {ADJ_STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
          {/* Owner, s215: the date window on its own line (the dates bound the REGISTER DATE of every kind). */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">From</span>
              <input type="date" className={inputClass} value={from} onChange={(e) => onFilter(setFrom)(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-muted)]">To</span>
              <input type="date" className={inputClass} value={to} onChange={(e) => onFilter(setTo)(e.target.value)} />
            </label>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[rgb(var(--edge-rgb)/0.05)] px-4 py-3 text-sm">
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={hideVoided} onChange={(e) => setHideVoided(e.target.checked)} />
            <span>Hide voided and not received</span>
          </label>
          {filtersActive ? (
            <Button variant="ghost" onClick={clearFilters}>Clear filters</Button>
          ) : null}
        </div>
      </Card>

      {err ? (
        <Card>
          <div className="p-4 text-sm text-[var(--tint-red-text)]">{err}</div>
        </Card>
      ) : null}

      {data?.truncated ? (
        <Card>
          <div className="p-4 text-sm text-[var(--tint-warn-text)]">
            This view spans more rows than the register can page through exactly, so rows deep in the list may be missing from
            this page. Narrow the filters (a date range or a type) to see everything.
          </div>
        </Card>
      ) : null}

      {/* Counts describe the SERVER's result set; the hide toggle only removes rows from view and says so. */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 p-4 text-sm">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>
              <span className="font-semibold">{totalCount}</span>{' '}
              {totalCount === 1 ? 'document' : 'documents'}
              {filtersActive ? ' matching' : ''}
            </span>
            {hiddenCount > 0 ? (
              <span className="text-[var(--color-muted)]">{hiddenCount} hidden on this page</span>
            ) : null}
            {clientId && creditBalance !== null ? (
              <span className="text-[var(--color-muted)]">
                Available credit <span className="font-semibold text-[var(--color-text)]">{formatMoney(creditBalance)}</span>
              </span>
            ) : null}
          </div>
          {figureBlocks.length > 0 ? (
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {figureBlocks.map((f) => (
                <div key={f.key} className="min-w-0">
                  <div className={`text-xs font-medium ${KIND_TONE[f.kind].text}`}>{f.label}</div>
                  <div className="font-semibold">
                    {f.value}
                    {f.tail ? <span className="ml-1 text-xs font-normal text-[var(--color-muted)]">{f.tail}</span> : null}
                  </div>
                  {f.note ? <div className="text-xs text-[var(--color-muted)]">{f.note}</div> : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Card>

      {loading && !data ? (
        <Card><div className="p-4 text-sm text-[var(--color-muted)]">Loading…</div></Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="p-6 text-center text-sm text-[var(--color-muted)]">
            {filtersActive ? 'No documents match these filters.' : 'No money has been recorded yet.'}
          </div>
        </Card>
      ) : visible.length === 0 ? (
        <Card>
          <div className="p-6 text-center text-sm text-[var(--color-muted)]">
            Every document on this page is voided or not received. Untick the hide toggle to see them.
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((r) => (r.kind === 'payment' ? renderPaymentRow(r) : renderAdjustmentRow(r)))}
        </div>
      )}

      {rows.length > 0 ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
            <label className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-muted)]">Rows per page</span>
              <select
                className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-1 text-sm"
                value={limit}
                onChange={(e) => onFilter(setLimit)(Number(e.target.value))}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            {totalPages > 1 ? (
              <div className="flex items-center gap-3">
                <Button variant="secondary" disabled={loading || (data?.page ?? 1) <= 1} onClick={() => setPage((n) => Math.max(1, n - 1))}>
                  Previous
                </Button>
                {/* The server CLAMPS an out-of-range page and echoes what it served. */}
                <span className="text-[var(--color-muted)]">Page {data?.page ?? 1} of {totalPages}</span>
                <Button variant="secondary" disabled={loading || (data?.page ?? 1) >= totalPages} onClick={() => setPage((n) => n + 1)}>
                  Next
                </Button>
              </div>
            ) : (
              <span className="text-[var(--color-muted)]">
                {totalCount === 1 ? 'One document fits' : 'All matching documents fit'} on a single page.
              </span>
            )}
          </div>
        </Card>
      ) : null}

      {/* ── New client credit ────────────────────────────────────────────────────────────────────── */}
      {newCreditForm ? (
        <ConfirmDialog open={true} title="Issue a client credit" confirmLabel="Issue credit" busy={busy} onConfirm={doNewCredit} onCancel={() => setNewCreditForm(null)}>
          <div className="space-y-3 text-left">
            <p className="text-sm">A standalone account credit. Usable on a future invoice, or applied to an open one from the credit's own dialog here.</p>
            <Field label="Client" required>
              <select className={inputClass} value={newCreditForm.clientId} onChange={(e) => setNewCreditForm((f) => (f ? { ...f, clientId: e.target.value } : f))}>
                <option value="">Choose a client</option>
                {clientOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </Field>
            <Field label="Amount" required>
              <input className={inputClass} inputMode="decimal" value={newCreditForm.amount} onChange={(e) => setNewCreditForm((f) => (f ? { ...f, amount: e.target.value } : f))} />
            </Field>
            <Field label="Reason" required>
              <input className={inputClass} value={newCreditForm.reason} maxLength={500} onChange={(e) => setNewCreditForm((f) => (f ? { ...f, reason: e.target.value } : f))} />
            </Field>
            {newCreditError ? <div className={errorBoxClass}>{newCreditError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {/* ── The per-document dialog (ruling 10 as amended s213) ─────────────────────────────────────── */}
      {doc ? (() => {
        const a = doc.fresh ?? doc.row.adjustment;
        const kind = doc.row.kind;
        const dead = a.status === 'voided';
        const remaining = creditRemaining(a);
        const apps = applicationsOf(a, invoiceNumberOf);
        const invoiceNumber = doc.invoice?.documentNumber ?? (a.invoiceId ? invoiceNumberOf(a.invoiceId) : null);
        // Blocked-by-order cases, each with its step. The server enforces every one; the dialog states
        // the order first so the admin is not sent into a 409 to learn it.
        const earlyPayViaPayment = kind === 'early-pay-discount' && !!a.sourcePaymentId;
        const overpaymentViaPayment = kind === 'client-credit' && !!a.sourcePaymentId;
        const creditInUse = kind === 'client-credit' && (a.amountApplied ?? 0) > 0;
        // A credit note paired with a refund: live refund on this page -> offer the refund's void (the
        // cascade voids this note in the same step); refund known voided -> a direct void; refund not on
        // this page -> offer Void and let the server's own order message answer.
        const linkedRefundRow = kind === 'credit-note' && a.linkedRefundId
          ? rows.find((r): r is AdjustmentRow => r.kind === 'refund' && r.adjustment.id === a.linkedRefundId) ?? null
          : null;
        const linkedRefundLive = linkedRefundRow ? linkedRefundRow.adjustment.status !== 'voided' : null;
        const paymentPanelInvoiceId = kind === 'client-credit' ? a.sourceInvoiceId ?? null : a.invoiceId ?? null;
        const canApply = !dead && kind === 'client-credit' && remaining > 0;
        const canVoid = !dead && !earlyPayViaPayment && !overpaymentViaPayment && !creditInUse && linkedRefundLive !== true;
        const detailRow = (label: string, value: JSX.Element | string | null): JSX.Element | null =>
          value === null || value === '' ? null : (
            <div className="flex flex-wrap justify-between gap-x-4 gap-y-0.5">
              <span className="text-[var(--color-muted)]">{label}</span>
              <span className="min-w-0 text-right">{value}</span>
            </div>
          );
        return createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={`${KIND_LABEL[kind]} ${a.documentNumber ?? ''}`}>
            <div className="absolute inset-0 glass-scrim" onClick={busy ? undefined : closeDoc} aria-hidden="true" />
            <div className="relative flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] shadow-xl">
              <div className="flex items-start justify-between gap-3 px-6 pb-2 pt-6">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`${chipClass} ${KIND_TONE[kind].chip}`}>{KIND_LABEL[kind]}</span>
                    {dead ? <span className={`${chipClass} bg-[rgb(var(--edge-rgb)/0.06)] text-[var(--color-muted)]`}>Voided</span> : null}
                  </div>
                  <h2 className="mt-1 break-all font-display text-xl">{a.documentNumber ?? 'Unnumbered'}</h2>
                  <div className="mt-1 text-xs text-[var(--color-muted)]">
                    {kind === 'client-credit' ? 'Issued' : kind === 'refund' && a.dateRefunded ? 'Refunded' : 'Dated'} {displayDate(doc.row.date)} · {clientName(a.clientId)}
                    {doc.fresh ? null : doc.error ? null : ' · reading the latest…'}
                  </div>
                </div>
                <button type="button" className="shrink-0 rounded p-1 text-[var(--color-muted)] hover:bg-[rgb(var(--edge-rgb)/0.05)]" aria-label="Close" onClick={closeDoc}>✕</button>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto px-6 py-2 text-sm">
                {doc.error ? <div className={errorBoxClass}>{doc.error}</div> : null}
                <div className="space-y-1">
                  {detailRow('Amount', <span className={`font-semibold ${dead ? 'line-through' : ''}`}>-{formatMoney(a.amount)}</span>)}
                  {kind === 'client-credit' ? detailRow('Applied so far', formatMoney(round2(a.amountApplied ?? 0))) : null}
                  {kind === 'client-credit' && !dead ? detailRow('Left to apply', <span className="font-semibold">{formatMoney(remaining)}</span>) : null}
                  {kind === 'refund' ? detailRow('Method', a.method ? METHOD_LABEL[a.method] ?? a.method : null) : null}
                  {kind === 'refund' && a.dateRefunded ? detailRow('Date refunded', displayDate(a.dateRefunded)) : null}
                  {a.invoiceId ? detailRow(kind === 'refund' ? 'Refund on' : kind === 'write-off' ? 'Written off on' : 'On invoice', <>{invoiceLink(a.invoiceId)}{invoiceNumber && !invoiceRef(a.invoiceId) ? ` (${invoiceNumber})` : ''}</>) : null}
                  {kind === 'client-credit' && a.sourceInvoiceId ? detailRow('Overpayment on', invoiceLink(a.sourceInvoiceId)) : null}
                  {a.sourcePaymentNumber ? detailRow(kind === 'early-pay-discount' ? 'Earned by payment' : kind === 'client-credit' ? 'From payment' : 'Leftover of payment', a.sourcePaymentNumber) : null}
                  {kind === 'refund' && a.linkedCreditNoteNumber ? detailRow('Paired credit note', a.linkedCreditNoteNumber) : null}
                  {kind === 'credit-note' && a.linkedRefundNumber ? detailRow('Paired refund', a.linkedRefundNumber) : null}
                  {(kind === 'credit-note' || kind === 'early-pay-discount') && a.creditedSubtotal !== undefined ? detailRow('Credited subtotal', formatMoney(a.creditedSubtotal)) : null}
                  {(kind === 'credit-note' || kind === 'early-pay-discount') && (a.discountReversed ?? 0) > 0.005 ? detailRow('Discount reversed', formatMoney(a.discountReversed ?? 0)) : null}
                  {(kind === 'credit-note' || kind === 'early-pay-discount') && a.taxReversed !== undefined ? detailRow('Tax reversed', formatMoney(a.taxReversed)) : null}
                  {a.lines && a.lines.length > 0 ? detailRow('Lines credited', `${a.lines.length}`) : null}
                  {detailRow('Reason', a.reason || null)}
                  {dead ? detailRow('Voided', <>{a.voidedAt ? displayDate(a.voidedAt.slice(0, 10)) : 'yes'}{a.voidReason ? ` · ${a.voidReason}` : ''}</>) : null}
                </div>
                <div className="text-xs text-[var(--color-muted)]">{cueFor(doc.row)}</div>
                {kind === 'client-credit' && apps.length > 0 ? (
                  <div className="border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2">
                    <h3 className="mb-1 font-medium">Applied to</h3>
                    <div className="space-y-0.5 text-xs">
                      {apps.map((x) => (
                        <div key={x.applicationId} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                          <span className="min-w-0 text-[var(--color-muted)]">
                            {displayDate(x.appliedAt.slice(0, 10))} · {invoiceLink(x.invoiceId) ?? <span className="font-medium">{x.invoiceNumber ?? x.invoiceId}</span>}
                          </span>
                          <span className="shrink-0">{formatMoney(x.amount)}</span>
                        </div>
                      ))}
                    </div>
                    {!dead ? (
                      <p className="mt-1 text-xs text-[var(--color-muted)]">To take an application back, open that invoice and use Un-apply on its credit line.</p>
                    ) : null}
                  </div>
                ) : null}
                {/* The order a blocked void has to follow, stated here rather than discovered as a 409. */}
                {!dead && earlyPayViaPayment ? (
                  <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-2 text-xs text-[var(--color-muted)]">
                    An early-pay discount is reversed by voiding the payment that earned it, {a.sourcePaymentNumber ?? 'its payment'}; voiding that payment voids this discount with it.
                    {paymentPanelInvoiceId ? <> Open {invoiceLink(paymentPanelInvoiceId, 'payments')} to find the payment.</> : null}
                  </div>
                ) : null}
                {!dead && overpaymentViaPayment ? (
                  <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-2 text-xs text-[var(--color-muted)]">
                    This credit is the overpayment on {a.sourcePaymentNumber ?? 'a payment'}. To reverse it, {creditInUse ? 'first un-apply it from the invoices above, then ' : ''}void that payment (which voids this credit too), or apply the credit to an invoice.
                    {paymentPanelInvoiceId ? <> Open {invoiceLink(paymentPanelInvoiceId, 'payments')} to find the payment.</> : null}
                  </div>
                ) : null}
                {!dead && creditInUse && !overpaymentViaPayment ? (
                  <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-2 text-xs text-[var(--color-muted)]">
                    This credit has been applied. To void it, first un-apply it from each invoice above.
                  </div>
                ) : null}
                {!dead && linkedRefundLive === true && linkedRefundRow ? (
                  <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-2 text-xs text-[var(--color-muted)]">
                    This credit note is paired with refund {a.linkedRefundNumber ?? linkedRefundRow.adjustment.documentNumber ?? ''}, which is still live. The refund is voided first, and its void can take this credit note with it in the same step.
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[rgb(var(--edge-rgb)/0.05)] px-6 py-4">
                {canApply ? (
                  <PillButton tone="green" disabled={busy || !doc.fresh} onClick={() => openApply(a)}>Apply to invoices</PillButton>
                ) : null}
                {!dead && linkedRefundLive === true && linkedRefundRow ? (
                  <PillButton tone="red" disabled={busy || !doc.fresh} onClick={() => startVoid(linkedRefundRow.adjustment)}>
                    Void refund {linkedRefundRow.adjustment.documentNumber ?? ''}
                  </PillButton>
                ) : null}
                {canVoid ? (
                  <PillButton tone="red" disabled={busy || !doc.fresh} onClick={() => startVoid(a)}>Void</PillButton>
                ) : null}
                <Button type="button" variant="ghost" onClick={closeDoc}>Close</Button>
              </div>
            </div>
          </div>,
          document.body,
        );
      })() : null}

      {/* ── Void (the former Invoices-page dialog, verbatim semantics) ──────────────────────────────── */}
      {voidTarget ? (
        <ConfirmDialog
          open={true}
          title={`Void ${KIND_LABEL[voidTarget.kind as MoneyRegisterKind] ?? 'this document'} ${voidTarget.documentNumber ?? ''}?`}
          confirmLabel="Void"
          confirmVariant="danger"
          busy={busy}
          onConfirm={doVoid}
          onCancel={() => { setVoidTarget(null); setVoidReason(''); setVoidError(null); }}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              Voiding keeps the record but removes its effect{voidTarget.invoiceId ? ' and reopens its invoice if a balance returns' : ''}. This can't be
              undone. Re-issue if you need it back.
            </p>
            <Field label="Reason" required>
              <input className={inputClass} autoFocus value={voidReason} maxLength={500} onChange={(e) => setVoidReason(e.target.value)} />
            </Field>
            {voidTarget.kind === 'refund' && voidTarget.linkedCreditNoteId ? (
              <>
                <label className="flex items-start gap-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-2 text-sm">
                  <input type="checkbox" className="mt-0.5 shrink-0" checked={voidAlsoCN} onChange={(e) => setVoidAlsoCN(e.target.checked)} />
                  <span>Also void the linked credit note {voidTarget.linkedCreditNoteNumber ?? ''} (voided right after this refund, so the trail reads refund then credit note). Leave off to void only the refund.</span>
                </label>
                {/* 04 #73 lock 8 (s99): unchecking is allowed but WARNED, naming the exact residual state. */}
                {!voidAlsoCN ? (
                  <div className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] p-2 text-sm text-[var(--tint-warn-text)]">
                    With the credit note left in place, the invoice stays settled by that credit while no cash has been returned. Reconciliation will flag it as over-funded until a replacement refund is recorded.
                  </div>
                ) : null}
              </>
            ) : null}
            {voidError ? <div className={errorBoxClass}>{voidError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {/* ── The credit-first Apply picker (04 #73(b); 04b #88) ─────────────────────────────────────── */}
      {applyForm ? (() => {
        const adj = applyForm.adjustment;
        const remaining = creditRemaining(adj);
        const applying = round2(applyForm.rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0));
        const remainder = round2(Math.max(0, remaining - applying));
        const overPot = applying > remaining + 0.001;
        return (
          <ConfirmDialog open={true} title="Apply credit to invoices" confirmLabel="Apply credit" busy={busy} confirmDisabled={applyForm.loading || overPot} size="lg" onConfirm={() => doApply()} onCancel={() => setApplyForm(null)}>
            <div className="space-y-3 text-left">
              <p className="text-sm">
                Applying up to <span className="font-semibold">{formatMoney(remaining)}</span> from {adj.documentNumber ?? 'this credit'}. Amounts are prefilled oldest-first; edit any row. Whatever is not applied stays on the credit.
              </p>
              {applyForm.loading ? (
                <p className="text-sm text-[var(--color-muted)]">Loading open invoices…</p>
              ) : applyForm.rows.length === 0 ? (
                <p className="text-sm text-[var(--color-muted)]">No open invoices for this billing entity right now.</p>
              ) : (
                <div className="space-y-2">
                  <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-[var(--color-border)] p-2">
                    {applyForm.rows.map((r) => (
                      <div key={r.invoiceId} className="grid grid-cols-[1fr_auto] items-center gap-2 text-sm">
                        <div className="min-w-0">
                          <span className={r.pastDue ? 'font-medium text-[var(--tint-red-text)]' : 'font-medium'}>{r.documentNumber ?? r.invoiceId}</span>
                          <span className="text-xs text-[var(--color-muted)]">
                            {' · '}{clientName(r.clientId)}
                            {' · issued '}{displayDate(r.issueDate)}{' · due '}
                            <span className={r.pastDue ? 'text-[var(--tint-red-text)]' : ''}>{displayDate(r.dueDate)}{r.pastDue ? ' (past due)' : ''}</span>
                            {' · '}{formatMoney(r.remaining)}{' due'}
                          </span>
                        </div>
                        <input
                          className={`${inputClass} w-28 text-right`}
                          inputMode="decimal"
                          value={r.amount}
                          onChange={(e) => setApplyRowAmount(r.invoiceId, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-sm">
                    <span>Applying: <span className={`font-semibold ${overPot ? 'text-[var(--tint-red-text)]' : ''}`}>{formatMoney(applying)}</span></span>
                    <span>{formatMoney(remainder)} will remain on {adj.documentNumber ?? 'the credit'}</span>
                  </div>
                  {overPot ? <div className={errorBoxClass}>The total to apply exceeds the credit's remaining {formatMoney(remaining)}.</div> : null}
                </div>
              )}
              {applyError ? <div className={errorBoxClass}>{applyError}</div> : null}
            </div>
          </ConfirmDialog>
        );
      })() : null}

      {releaseHeadsUp ? (
        <ConfirmDialog
          open={true}
          title="Release documents to the client?"
          confirmLabel="Apply and release"
          busy={busy}
          onConfirm={() => releaseHeadsUp.retry()}
          onCancel={() => setReleaseHeadsUp(null)}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              This credit fully settles the invoice, which releases {releaseHeadsUp.documents.length} pay-on-delivery
              document{releaseHeadsUp.documents.length === 1 ? '' : 's'} to the client:
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {releaseHeadsUp.documents.map((d) => <li key={d.id}>{d.title}</li>)}
            </ul>
            <p className="text-xs text-[var(--color-muted)]">Continue to apply the credit and release the documents, or cancel to leave the invoice as it is.</p>
          </div>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
